//@ts-strict-ignore
import React, { useContext, useRef } from 'react';
import { Panel, PanelType } from '@fluentui/react/lib/Panel';
import Row from '../connectedForm/Row';
import CompanyUserNotificationListQuery from '../../graphql/queries/company/CompanyUserNotificationListQuery.graphql';
import {
  AlertType,
  Direction,
  FilterOperation,
  ICompanyUserNotification,
  ICompanyUserNotificationListQueryResult,
  ICompanyUserNotificationListQueryVariables,
  IFilterInput,
} from '../../graphql/schema/p4types';
import { List } from '@fluentui/react';
import { FetchMoreOptions, FetchMoreQueryOptions, useQuery } from '@apollo/client';
import UserContext from '../Context/UserContext';
import { DismissedNotification } from '../Notification/DismissedNotification';
import styled from 'styled-components';
import { Drawer, DrawerBody, DrawerHeader, DrawerHeaderTitle, makeStyles } from '@fluentui/react-components';
import { p4Tokens } from '../../styles/themes/default/p4FluentTheme';

type fetchMoreBasicType = {
  fetchMore: null | ((fetchMoreOptions: FetchMoreQueryOptions<any, any> & FetchMoreOptions<any, any>) => Promise<any>);
};

const ActionCentreContext = React.createContext({ fetchMore: null } as fetchMoreBasicType);

const NotificationCell = ({
  notification,
  index,
}: {
  notification: { uuid: string; title: string; message: string; alertType: AlertType };
  index?: number;
}) => {
  const requestedPage = useRef(false);
  const { fetchMore } = useContext(ActionCentreContext);
  const { currentUser } = useContext(UserContext);
  const filters: IFilterInput[] = [
    {
      filterOperation: FilterOperation.Equals,
      filterParameters: {
        field: 'companyUser.id',
        value: currentUser.companyUser.id,
      },
    },
  ];

  const pageIndex = Math.floor((index ?? 0) / 30);

  if (notification.uuid) {
    return (
      <Row key={notification.uuid}>
        <DismissedNotification
          type={notification.alertType}
          message={notification.message || ''}
          title={notification.title || ''}
        />
      </Row>
    );
  }
  if (!requestedPage.current) {
    requestedPage.current = true;

    fetchMore?.({
      variables: {
        orderBy: 'createdAt',
        orderDirection: Direction.Desc,
        first: 30,
        after: `${pageIndex * 30}`,
        filters,
      },
    });
  }

  return <div style={{ height: '50px', backgroundColor: 'grey' }} />;
};

const onRenderCell = (item, index: number | undefined): JSX.Element | null => {
  return item ? <NotificationCell notification={item} index={index} /> : null;
};

const ActionCentrePanel = ({
  panelOpen,
  setPanelOpen,
}: {
  panelOpen: boolean;
  setPanelOpen: (po: boolean) => void;
}) => {
  const { currentUser } = useContext(UserContext);
  const filters: IFilterInput[] = [
    {
      filterOperation: FilterOperation.Equals,
      filterParameters: {
        field: 'companyUser.id',
        value: currentUser.companyUser?.id,
      },
    },
  ];

  const { data: notificationData, fetchMore } = useQuery<
    ICompanyUserNotificationListQueryResult & { list: { count: number } },
    ICompanyUserNotificationListQueryVariables
  >(CompanyUserNotificationListQuery, {
    variables: { orderBy: 'createdAt', orderDirection: Direction.Desc, first: 30, filters }, //IMPORTANT - If these variables change, they need to change in Notification.tsx in the mutation cache update too!
  });

  if (!notificationData) return null;

  const notifications = notificationData.list?.edges?.map((edge) => edge?.node) as Array<
    ICompanyUserNotification | { index: number }
  >;
  while (notifications?.length < notificationData.list?.count) {
    notifications.push({ index: notifications.length });
  }

  return (
    <Drawer open={panelOpen} position={'end'} size={'medium'} onOpenChange={(e, data) => setPanelOpen(data.open)}>
      <DrawerHeader>
        <DrawerHeaderTitle>Recent Notifications</DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        {notifications.length === 0 ? (
          'No recent notifications found'
        ) : (
          <ActionCentreContext.Provider value={{ fetchMore }}>
            <List items={notifications} onRenderCell={onRenderCell} />
          </ActionCentreContext.Provider>
        )}
      </DrawerBody>
    </Drawer>
  );
};

export default ActionCentrePanel;
